import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';

import { AppButton, AppCard, AppSpin } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useRezeptInvoiceInit } from '@/hooks/main/useRezeptInvoiceInit';
import { useRezeptInvoiceMassagers } from '@/hooks/main/useRezeptInvoiceMassagers';
import { useRezeptInvoiceList } from '@/hooks/main/useRezeptInvoiceList';
import { useRezeptInvoiceListPdf } from '@/hooks/main/useRezeptInvoiceListPdf';
import { useRezeptFormatPdf } from '@/hooks/main/useRezeptFormatPdf';
import type { RezeptInvoiceListFilters } from '@/services/main/rezept';
import type { RezeptInvoiceSearchFormValues } from '@/schemas/rezeptInvoiceSearchSchema';
import {
  rezeptInvoicePrintOptionsSchema,
  DEFAULT_PRINT_OPTIONS,
  getDefaultPrintOptions,
  type RezeptInvoicePrintOptionsFormValues,
} from '@/schemas/rezeptInvoicePrintOptionsSchema';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import RezeptInvoiceSearchFilter from './components/RezeptInvoiceSearchFilter';
import RezeptInvoiceListTable from './components/RezeptInvoiceListTable';
import RezeptInvoicePrintOptionsPanel from './components/RezeptInvoicePrintOptionsPanel';
import {
  formValuesToSearchParams,
  searchParamsToFormValues,
  searchParamsToPage,
  searchParamsToPerPage,
} from './urlParams';

const DEFAULT_PER_PAGE = 20;

function toApiFilters(
  values: RezeptInvoiceSearchFormValues,
  page: number,
  perPage: number
): RezeptInvoiceListFilters {
  const massagerId = values.massagerId && values.massagerId > 0 ? values.massagerId : null;
  return {
    search_term_type: values.searchTermType,
    search_treatement_from: values.searchTreatementFrom ?? null,
    search_treatement_to: values.searchTreatementTo ?? null,
    search_treatement_one: values.searchTreatementOne ?? null,
    massager_id: massagerId,
    sort_radio: values.sortRadio,
    page,
    per_page: perPage,
  };
}

const RezeptInvoiceListPage: React.FC = () => {
  const { serviceType } = useTheme();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [formValues, setFormValues] = useState<RezeptInvoiceSearchFormValues | null>(null);
  const [page, setPage] = useState(() => searchParamsToPage(urlSearchParams));
  const [perPage, setPerPage] = useState(() =>
    searchParamsToPerPage(urlSearchParams, DEFAULT_PER_PAGE)
  );
  const [tableResetKey, setTableResetKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const toast = useAppToast();
  const sidebarOpen = useSidebarOpen();

  // Reset state when service type actually changes (A2 ↔ R2).
  // setUrlSearchParams is intentionally excluded from deps: its identity changes
  // on every URL update, which would re-fire this and wipe a fresh search.
  const lastServiceTypeRef = useRef(serviceType);
  useEffect(() => {
    if (lastServiceTypeRef.current === serviceType) return;
    lastServiceTypeRef.current = serviceType;
    setFormValues(null);
    setPage(1);
    setPerPage(DEFAULT_PER_PAGE);
    setSelectedIds([]);
    setUrlSearchParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  // Init: fetch defaults for the search form on screen entry.
  const { data: initData, isLoading: isInitLoading } = useRezeptInvoiceInit();

  // Massager dropdown is keyed by treatement_yearmonth — derived from the
  // 施術開始期 (search_treatement_from) field. BD: reload when user picks a
  // new `from` date; do not reload on radio switch / `to` / 日別 changes.
  const [treatmentYearmonth, setTreatmentYearmonth] = useState<string>(() => {
    const fromUrl = urlSearchParams.get('search_treatement_from');
    if (fromUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl)) {
      const ym = dayjs(fromUrl).format('YYYYMM');
      if (/^\d{6}$/.test(ym)) return ym;
    }
    return dayjs().format('YYYYMM');
  });
  // After init resolves, sync from the BE-provided default in case it differs
  // from current month (e.g., session-restored date). URL value wins if present.
  useEffect(() => {
    if (urlSearchParams.has('search_treatement_from')) return;
    const from = initData?.data?.defaults?.search_treatement_from;
    if (!from) return;
    const ym = dayjs(from).format('YYYYMM');
    if (/^\d{6}$/.test(ym)) setTreatmentYearmonth(ym);
  }, [initData, urlSearchParams]);

  const { data: massagersData } = useRezeptInvoiceMassagers(treatmentYearmonth);

  const printOptionsForm = useTrimmedForm<RezeptInvoicePrintOptionsFormValues>({
    resolver: zodResolver(rezeptInvoicePrintOptionsSchema),
    defaultValues: DEFAULT_PRINT_OPTIONS,
    mode: 'onSubmit',
  });

  const pdfMutation = useRezeptInvoiceListPdf();
  const formatPdfMutation = useRezeptFormatPdf();

  const massagerOptions = useMemo(
    () =>
      (massagersData?.data?.massagers ?? []).map(m => ({
        value: m.id,
        label:
          m.serial_number != null && m.serial_number !== ''
            ? `${m.serial_number}:${m.name ?? ''}`
            : (m.name ?? `#${m.id}`),
      })),
    [massagersData]
  );

  // Form initial values: hydrate from URL if query present, else use BE defaults.
  // urlSearchParams is intentionally excluded from deps — initialValues are only
  // consumed by the search filter on first mount, and re-computing on every URL
  // change would just churn without effect.
  const initialFormValues = useMemo<Partial<RezeptInvoiceSearchFormValues> | undefined>(() => {
    const d = initData?.data?.defaults;
    const fromDefaults: Partial<RezeptInvoiceSearchFormValues> | undefined = d
      ? {
          searchTermType: d.search_term_type,
          searchTreatementFrom: d.search_treatement_from,
          searchTreatementTo: d.search_treatement_to,
          searchTreatementOne: dayjs().format('YYYY-MM-DD'),
        }
      : undefined;
    return searchParamsToFormValues(urlSearchParams, fromDefaults) ?? fromDefaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData]);

  // On first mount, if URL already has filter params → kick off a search automatically.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!initData) return;
    hydratedRef.current = true;
    const fromUrl = searchParamsToFormValues(urlSearchParams, initialFormValues);
    if (!fromUrl) return;
    setFormValues(fromUrl);
    printOptionsForm.reset(getDefaultPrintOptions(fromUrl.searchTermType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData]);

  const filters = useMemo<RezeptInvoiceListFilters | undefined>(
    () => (formValues ? toApiFilters(formValues, page, perPage) : undefined),
    [formValues, page, perPage]
  );

  const { data, isLoading, isFetching } = useRezeptInvoiceList(filters, { enabled: !!filters });

  const items = data?.data?.data ?? [];
  const pagination = data?.data?.pagination ?? null;
  const allIds = data?.data?.all_ids ?? [];
  const orderedSelectedIds = useMemo(() => {
    if (selectedIds.length === 0) return [];
    const selectedSet = new Set(selectedIds);
    return allIds.filter(id => selectedSet.has(id));
  }, [allIds, selectedIds]);

  const handleSearch = (values: RezeptInvoiceSearchFormValues) => {
    setPage(1);
    setFormValues(values);
    setTableResetKey(key => key + 1);
    setSelectedIds([]);
    printOptionsForm.reset(getDefaultPrintOptions(values.searchTermType));
    setUrlSearchParams(formValuesToSearchParams(values, 1, perPage, DEFAULT_PER_PAGE), {
      replace: true,
    });
  };

  const handleChangePage = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPerPage(nextPageSize);
    if (formValues) {
      setUrlSearchParams(
        formValuesToSearchParams(formValues, nextPage, nextPageSize, DEFAULT_PER_PAGE),
        { replace: true }
      );
    }
  };

  const runPrint = useCallback(
    (rezeptIds: number[], values: RezeptInvoicePrintOptionsFormValues) => {
      const pdfWindow = window.open('about:blank', '_blank');
      pdfMutation.mutate(
        {
          rezept_ids: rezeptIds,
          print_receipt: values.print_receipt,
          print_invoice: values.print_invoice,
          print_receipt_invoice: values.print_receipt_invoice,
          print_type: values.print_type as 1 | 2 | 3,
          search_term_type: (formValues?.searchTermType ?? 1) as 1 | 2,
          print_receipt_100_sub1: values.print_receipt_100_sub1,
          print_receipt_100_sub2: values.print_receipt_100_sub2,
          print_receipt_100_sub3: values.print_receipt_100_sub3,
          option_1: values.option_1,
          option_2: values.option_2,
          option_3: values.option_3,
          option_4: values.option_4,
          option_5: values.option_5,
          option_6: values.option_6,
          option_7: values.option_7,
          option_8: values.option_8,
          option_9: values.option_9,
          option_11: values.option_11,
          option_date: values.option_date,
          // 月別: pass from/to; 日別: searchTreatementOne mirrored into from/to by the
          // search filter, so passing from = to = selected day is enough.
          search_treatement_from:
            (formValues?.searchTermType === 2
              ? formValues?.searchTreatementOne
              : formValues?.searchTreatementFrom) ?? undefined,
          search_treatement_to:
            (formValues?.searchTermType === 2
              ? formValues?.searchTreatementOne
              : formValues?.searchTreatementTo) ?? undefined,
        },
        {
          onSuccess: url => {
            if (pdfWindow) {
              pdfWindow.location.href = url;
            } else {
              window.open(url, '_blank');
            }
          },
          onError: err => {
            if (pdfWindow) pdfWindow.close();
            toast.error(getApiErrorMessage(err, '印刷に失敗しました。'));
          },
        }
      );
    },
    [
      pdfMutation,
      toast,
      formValues?.searchTermType,
      formValues?.searchTreatementFrom,
      formValues?.searchTreatementTo,
      formValues?.searchTreatementOne,
    ]
  );

  const handlePrint = useCallback(
    printOptionsForm.handleSubmit(
      values => {
        if (orderedSelectedIds.length === 0) {
          toast.error('受診者を選択してください。');
          return;
        }
        runPrint(orderedSelectedIds, values);
      },
      errors => {
        const msg =
          errors.print_receipt?.message ||
          errors.print_type?.message ||
          errors.option_date?.message;
        if (msg) toast.error(String(msg));
      }
    ),
    [printOptionsForm, orderedSelectedIds, runPrint, toast]
  );

  // フォーマット印刷 (BD): print an **empty** template (no rezept data filled).
  //   - No need to tick Section 3 rows (not related to a specific rezept).
  //   - Validate Print Settings: >=1 form (領収書/請求書/兼用) + print_type
  //     + option_date (if option_5 is ticked).
  //   - Do not update 済 and do not apply the 10割 override (because there is no data).
  //   - BE renders forms in the order 領収書 -> 請求書 -> 兼用 when multiple forms are ticked.
  const handleFormatPrint = useCallback(
    printOptionsForm.handleSubmit(
      values => {
        const pdfWindow = window.open('about:blank', '_blank');
        formatPdfMutation.mutate(
          {
            print_receipt: values.print_receipt,
            print_invoice: values.print_invoice,
            print_receipt_invoice: values.print_receipt_invoice,
            print_type: values.print_type as 1 | 2 | 3,
            search_term_type: (formValues?.searchTermType ?? 1) as 1 | 2,
            print_receipt_100_sub1: values.print_receipt_100_sub1,
            print_receipt_100_sub2: values.print_receipt_100_sub2,
            print_receipt_100_sub3: values.print_receipt_100_sub3,
            option_1: values.option_1,
            option_2: values.option_2,
            option_3: values.option_3,
            option_4: values.option_4,
            option_5: values.option_5,
            option_6: values.option_6,
            option_7: values.option_7,
            option_8: values.option_8,
            option_9: values.option_9,
            option_11: values.option_11,
            option_date: values.option_date,
          },
          {
            onSuccess: url => {
              if (pdfWindow) pdfWindow.location.href = url;
              else window.open(url, '_blank');
            },
            onError: err => {
              if (pdfWindow) pdfWindow.close();
              toast.error(getApiErrorMessage(err, 'フォーマット印刷に失敗しました。'));
            },
          }
        );
      },
      errors => {
        const msg =
          errors.print_receipt?.message ||
          errors.print_type?.message ||
          errors.option_date?.message;
        if (msg) toast.error(String(msg));
      }
    ),
    [printOptionsForm, formatPdfMutation, toast, formValues?.searchTermType]
  );

  if (isInitLoading || !initData) {
    return (
      <div className="flex items-center justify-center p-8">
        <AppSpin />
      </div>
    );
  }

  const showPanel = !!formValues && items.length > 0;

  return (
    <div className="space-y-4">
      <RezeptInvoiceSearchFilter
        initialValues={initialFormValues}
        massagerOptions={massagerOptions}
        onSearch={handleSearch}
        loading={isFetching}
        onTreatmentFromYearmonthChange={setTreatmentYearmonth}
      />

      {formValues && (
        <AppCard className="shadow-sm">
          <RezeptInvoiceListTable
            items={items}
            pagination={pagination}
            allIds={allIds}
            loading={isLoading || isFetching}
            resetKey={tableResetKey}
            selectedIds={selectedIds}
            onChangeSelectedIds={setSelectedIds}
            onChangePage={handleChangePage}
            searchTermType={formValues.searchTermType}
            searchTreatmentOne={formValues.searchTreatementOne}
          />
        </AppCard>
      )}

      {showPanel && (
        <RezeptInvoicePrintOptionsPanel
          control={printOptionsForm.control}
          watch={printOptionsForm.watch}
          setValue={printOptionsForm.setValue}
          searchTermType={formValues?.searchTermType}
        />
      )}

      <BottomBar
        variant="action"
        visible={showPanel}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <div className="flex gap-2">
            <AppButton
              type="primary"
              onClick={handlePrint}
              loading={pdfMutation.isPending}
              className="w-24 rounded"
            >
              印刷
            </AppButton>
            <AppButton
              type="primary"
              loading={formatPdfMutation.isPending}
              onClick={handleFormatPrint}
              className="rounded w-24 shrink-0 !border-white !bg-white !text-slate-600 hover:!bg-slate-100 hover:!text-slate-700 hover:!border-slate-100"
            >
              雛形印刷
            </AppButton>
          </div>
        }
      />
    </div>
  );
};

export default RezeptInvoiceListPage;
